<template>
  <v-dialog
    :value="visible"
    content-class="add-task-dialog"
    persistent
    :retain-focus="false"
    data-testid="tasklist__add-task-dialog"
    @input="onDialogInput"
  >
    <v-card
      class="add-task-dialog__card d-flex flex-column"
      :loading="loading"
      :disabled="loading"
      flat
    >
      <div class="add-task-dialog__header">
        <span class="add-task-dialog__header-title">{{ dialogTitle }}</span>
        <v-btn
          icon
          x-small
          dark
          class="add-task-dialog__close-btn"
          data-testid="tasklist__add-task-dialog__close-btn"
          @click="close"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- AR stepper (Details → Account) — Assignment lives on Details pane -->
      <div
        v-if="isAccountReconciliation"
        class="add-task-dialog__stepper"
        data-testid="tasklist__add-task-dialog__stepper"
      >
        <div class="add-task-dialog__steps">
          <template v-for="(step, i) in arSteps">
            <button
              :key="`step-${step.id}`"
              type="button"
              class="add-task-dialog__step"
              :class="{
                'add-task-dialog__step--active': activeStep === step.id,
                'add-task-dialog__step--done': isStepDone(step.id),
              }"
              :data-testid="`tasklist__add-task-dialog__step--${step.id}`"
              @click="onStepClick(step.id)"
            >
              <span
                class="add-task-dialog__step-circle"
                :class="{
                  'add-task-dialog__step-circle--active': activeStep === step.id,
                  'add-task-dialog__step-circle--done': isStepDone(step.id),
                }"
              >
                <v-icon v-if="isStepDone(step.id)" small dark>mdi-check</v-icon>
                <span v-else>{{ i + 1 }}</span>
              </span>
              <span class="add-task-dialog__step-label">{{ step.label }}</span>
            </button>
            <div
              v-if="i < arSteps.length - 1"
              :key="`conn-${step.id}`"
              class="add-task-dialog__connector"
              :class="{ 'add-task-dialog__connector--done': isStepDone(step.id) }"
            />
          </template>
        </div>
      </div>

      <div ref="dialogBody" class="add-task-dialog__body">
        <!-- Checklist: two-pane layout -->
        <div v-if="isChecklistTaskType" class="add-task-dialog__panes">
          <div class="add-task-dialog__details-section">
            <div class="add-task-dialog__pane add-task-dialog__pane--left">
            <p class="add-task-dialog__section-label add-task-dialog__section-label--required">Details</p>
            <CollapsibleTextAreaField
              v-model="addTaskData.description"
              label="Task Name"
              variant="plain"
              :collapsible="false"
              :expanded="true"
              :required="true"
              :rows="5"
              :error="addTaskDataError.description"
              data-testid="tasklist__add-task-dialog__task-name"
              placeholder="e.g., Conduct Q3 Financial Audit"
            />

            <CollapsibleTextAreaField
              v-model="addTaskData.purpose"
              label="Purpose"
              variant="card"
              :expanded.sync="purposeExpanded"
              :rows="3"
              :error="addTaskDataError.purpose"
              :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.PURPOSE"
              data-testid="tasklist__add-task-dialog__purpose"
            />

            <CollapsibleTextAreaField
              v-model="addTaskData.instruction"
              label="Instruction"
              variant="card"
              :expanded.sync="instructionExpanded"
              :rows="3"
              :error="addTaskDataError.instruction"
              :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.INSTRUCTION"
              data-testid="tasklist__add-task-dialog__instruction"
            />

            <div class="add-task-dialog__details-block">
              <div class="add-task-dialog__evidence-block">
                <p class="add-task-dialog__field-label add-task-dialog__evidence-count-label">
                  {{ evidenceRequiredLabels.COUNT_LABEL }}
                </p>
                <div class="add-task-dialog__evidence-row">
                  <div class="add-task-dialog__evidence-count">
                    <v-text-field
                      v-model.number="addTaskData.no_of_evidence"
                      class="add-task-dialog__evidence-input"
                      type="number"
                      :min="evidenceRequiredLabels.COUNT_MIN"
                      :max="evidenceRequiredLabels.COUNT_MAX"
                      :step="1"
                      outlined
                      dense
                      hide-details="auto"
                      :error-messages="addTaskDataError.no_of_evidence"
                      data-testid="tasklist__add-task-dialog__no-of-evidences"
                      @input="onEvidenceInput"
                      @keypress="onEvidenceCountKeypress"
                      @paste="onEvidenceCountPaste"
                    />
                  </div>
                  <div class="add-task-dialog__evidence-required-cell">
                    <div class="add-task-dialog__evidence-required-group">
                      <p class="add-task-dialog__evidence-required-label">{{ evidenceRequiredLabels.LABEL }}</p>
                      <button
                        type="button"
                        class="add-task-dialog__sign-off-toggle"
                        :class="{ 'add-task-dialog__sign-off-toggle--on': addTaskData.evidence_required }"
                        :aria-checked="addTaskData.evidence_required ? 'true' : 'false'"
                        :aria-label="evidenceRequiredLabels.LABEL"
                        data-testid="tasklist__add-task-dialog__evidence-required-toggle"
                        @click="onEvidenceRequiredToggle"
                      >
                        <span class="add-task-dialog__sign-off-toggle-thumb" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="add-task-dialog__row">
                <div class="add-task-dialog__row-item">
                  <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--primary">Category</p>
                  <FreeformSelect
                    :key="`category-${addTaskData.task_type || 'none'}`"
                    v-model="addTaskData.category"
                    :items="categoryItemsForTaskType"
                    :error="addTaskDataError.category"
                    placeholder="Select"
                    data-testid="tasklist__add-task-dialog__category"
                  />
                </div>
                <div class="add-task-dialog__row-item">
                  <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--primary">Sub Category</p>
                  <FreeformSelect
                    :key="`subcategory-${addTaskData.task_type || 'none'}`"
                    v-model="addTaskData.subcategory"
                    :items="subcategoryItemsForTaskType"
                    :error="addTaskDataError.subcategory"
                    placeholder="Select"
                    data-testid="tasklist__add-task-dialog__subcategory"
                  />
                </div>
              </div>
              <div v-if="showEditTaskTypeField" class="add-task-dialog__row">
                <div class="add-task-dialog__row-item">
                  <p class="add-task-dialog__field-label">Task Type</p>
                  <lb-dropdown
                    v-if="isEditTaskTypeEditable"
                    v-model="addTaskData.task_type"
                    :multiple="false"
                    :items="editTaskTypeOptions"
                    itemtext="name"
                    itemvalue="value"
                    appendiconinner="mdi-chevron-down"
                    :clearable="false"
                    :searchable="false"
                    label=""
                    placeholder="Select"
                    :error="addTaskDataError.task_type"
                    data-testid="tasklist__add-task-dialog__task-type"
                  />
                  <v-text-field
                    v-else
                    :value="taskTypeDisplayLabel"
                    outlined
                    dense
                    readonly
                    disabled
                    hide-details
                    data-testid="tasklist__add-task-dialog__task-type"
                  />
                </div>
              </div>
              <div class="add-task-dialog__row">
                <div class="add-task-dialog__row-item">
                  <p class="add-task-dialog__field-label">Assertions</p>
                  <lb-tagsInput
                    v-model="addTaskData.assertions"
                    class="add-task-dialog__tags-input"
                    :hidedetails="true"
                    :items="assertionItems"
                    label=""
                    :error="addTaskDataError.assertions"
                    tagcolor="purpleborder"
                    :multiple="true"
                    :multiplechips="true"
                    :allow-overflow="true"
                    :menu-search="true"
                    :menu-offset-y="false"
                    menu-content-class="add-task-dialog__tags-menu"
                    search-placeholder="Search"
                    appendiconinner="mdi-chevron-down"
                    data-testid="tasklist__add-task-dialog__assertions"
                  />
                </div>
              </div>

              <div
                v-if="resolvedCustomColumns.length > 0"
                class="add-task-dialog__custom-grid"
              >
                <div
                  v-for="field in resolvedCustomColumns"
                  :key="field.columnName"
                  class="add-task-dialog__row-item"
                >
                  <p class="add-task-dialog__field-label add-task-dialog__field-label--primary">{{ field.displayText }}</p>
                  <lb-string
                    v-if="field.dataType === 'string'"
                    v-model="addTaskData[field.columnName]"
                    label=""
                    :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                    :error="addTaskDataError[field.columnName]"
                    :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                  />
                  <lb-date
                    v-else-if="field.dataType === 'date'"
                    v-model="addTaskData[field.columnName]"
                    label=""
                    :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                    :error="addTaskDataError[field.columnName]"
                    :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                  />
                  <lb-number
                    v-else-if="field.dataType === 'number'"
                    v-model="addTaskData[field.columnName]"
                    allow-empty
                    label=""
                    :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                    :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                  />
                  <lb-textBox
                    v-else
                    v-model="addTaskData[field.columnName]"
                    label=""
                    :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                    :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                  />
                </div>
              </div>
            </div>

            <div class="add-task-dialog__schedule">
              <p class="add-task-dialog__section-label">Schedule</p>
              <div>
                <p class="add-task-dialog__field-label add-task-dialog__field-label--required">Due Date</p>
                <AddTaskDueDateField
                  v-model="addTaskData.duedate"
                  :error="addTaskDataError.duedate"
                  :min="dueDateMinDate"
                  :max="dueDateMaxDate"
                  :blocked-due-before="blockedDueBefore"
                  :invalid-date-tooltip="calendarBlockedDueTooltip"
                  placeholder="Select Due Date"
                  data-testid="tasklist__add-task-dialog__due-date"
                />
              </div>
              <div class="add-task-dialog__priority">
                <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--bold">Priority</p>
                <TaskPriorityDisplay
                  variant="toggle-segment"
                  :value="addTaskData.priority"
                  data-testid="tasklist__add-task-dialog__priority"
                  @input="addTaskData.priority = $event"
                />
                <span v-if="addTaskDataError.priority" class="caption error--text">
                  {{ formatError(addTaskDataError.priority) }}
                </span>
              </div>
              <div class="add-task-dialog__tags">
                <p class="add-task-dialog__field-label">Frequency</p>
                <lb-tagsInput
                  v-model="addTaskData.tags"
                  class="add-task-dialog__tags-input"
                  tagcolor="purpleborder"
                  :multiple="true"
                  :multiplechips="true"
                  :items="allTags"
                  :hidedetails="true"
                  :menu-search="true"
                  :menu-offset-y="false"
                  menu-content-class="add-task-dialog__tags-menu"
                  search-placeholder="Search"
                  empty-message="No frequency found"
                  appendiconinner="mdi-chevron-down"
                  label=""
                  placeholder=""
                  :error="addTaskDataError.tags"
                  data-testid="tasklist__add-task-dialog__tags"
                />
              </div>
            </div>
          </div>
          <div class="add-task-dialog__pane-divider" role="separator"></div>
          <div class="add-task-dialog__pane add-task-dialog__pane--right">
            <p class="add-task-dialog__section-label">Assignment</p>
            <div :class="{ 'add-task-dialog__location-error': assignmentFieldError }">
              <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--primary">{{ locationHeader }}</p>
              <ChipMultiSelect
                v-model="addTaskData.location"
                :single="true"
                :clearable="true"
                variant="add-task"
                :items="actorCycleLocationList"
                item-text="name"
                item-value="_id"
                :searchable="true"
                search-placeholder="Search"
                :error="addTaskDataError.location"
                placeholder="Select"
                data-testid="tasklist__add-task-dialog__location"
                data-testid-prefix="tasklist__add-task-dialog__location"
              />
            </div>

            <div :class="{ 'add-task-dialog__field-error': !!addTaskDataError.preparer }">
              <UserChipMultiSelect
                :value="addTaskData.preparer"
                variant="add-task"
                label="Preparer(s)"
                :required="true"
                :users="preparerPickerUsers"
                :allowed-menu-ids="allowedUserIds"
                :exclude-ids="preparerExcludeIds"
                :loading="usersFetchLoading"
                role-label="preparer"
                placeholder="Select"
                :empty-message="assignmentUsersEmptyMessage"
                :attach="true"
                data-testid="tasklist__add-task-dialog__preparer"
                @input="onAssignmentRoleChange('preparer', $event)"
              />
              <span v-if="addTaskDataError.preparer" class="caption error--text">
                {{ formatError(addTaskDataError.preparer) }}
              </span>
            </div>

            <div :class="{ 'add-task-dialog__field-error': !!(addTaskDataError.approver || addTaskDataError.location_users) }">
              <UserChipMultiSelect
                :value="addTaskData.approver"
                variant="add-task"
                label="Approver(s)"
                :required="true"
                :users="approverPickerUsers"
                :allowed-menu-ids="allowedUserIds"
                :exclude-ids="approverExcludeIds"
                :loading="usersFetchLoading"
                role-label="approver"
                placeholder="Select"
                :empty-message="assignmentUsersEmptyMessage"
                :attach="true"
                data-testid="tasklist__add-task-dialog__approver"
                @input="onAssignmentRoleChange('approver', $event)"
              />
              <span v-if="addTaskDataError.approver || addTaskDataError.location_users" class="caption error--text">
                {{ formatError(addTaskDataError.approver || addTaskDataError.location_users) }}
              </span>
            </div>

            <div class="add-task-dialog__sign-off-section add-task-dialog__sign-off-edit-allowed">
              <div class="add-task-dialog__sign-off" data-testid="tasklist__add-task-sign-off-stub">
                <div class="add-task-dialog__sign-off-copy">
                  <p class="add-task-dialog__sign-off-title">{{ signOffLabels.REQUIRES_LABEL }}</p>
                  <p class="add-task-dialog__sign-off-desc">{{ signOffLabels.REQUIRES_DESC }}</p>
                </div>
                <button
                  type="button"
                  class="add-task-dialog__sign-off-toggle"
                  :class="{ 'add-task-dialog__sign-off-toggle--on': addTaskData.requires_sign_off }"
                  :aria-checked="addTaskData.requires_sign_off ? 'true' : 'false'"
                  :aria-label="signOffLabels.REQUIRES_LABEL"
                  data-testid="tasklist__add-task-dialog__requires-sign-off"
                  @click="onRequiresSignOffToggle"
                >
                  <span class="add-task-dialog__sign-off-toggle-thumb" />
                </button>
              </div>
              <template v-if="addTaskData.requires_sign_off">
                <div class="add-task-dialog__sign-off-picker">
                  <p class="add-task-dialog__sign-off-picker-label">{{ signOffLabels.APPROVERS_LABEL }}</p>
                  <p class="add-task-dialog__sign-off-hint ma-0">{{ signOffLabels.PICKER_HINT }}</p>
                  <UserChipMultiSelect
                    :value="addTaskData.sign_off_approvers"
                    variant="add-task"
                    label=""
                    :users="signOffPickerUsers"
                    :allowed-menu-ids="allowedUserIds"
                    :exclude-ids="signOffExcludeIds"
                    :loading="usersFetchLoading"
                    role-label="sign-off approver"
                    placeholder="Select"
                    :attach="true"
                    data-testid="tasklist__add-task-dialog__sign-off-approver"
                    @input="onAssignmentRoleChange('sign_off_approvers', $event)"
                  />
                  <span v-if="addTaskDataError.sign_off_approvers" class="caption error--text">
                    {{ formatError(addTaskDataError.sign_off_approvers) }}
                  </span>
                </div>
              </template>
            </div>

            <div class="add-task-dialog__sign-off-section">
              <div class="add-task-dialog__sign-off" data-testid="tasklist__add-task-dialog__auto-approve">
                <div class="add-task-dialog__sign-off-copy">
                  <p class="add-task-dialog__sign-off-title">{{ autoApproveLabels.LABEL }}</p>
                  <p class="add-task-dialog__sign-off-desc">{{ autoApproveDescription }}</p>
                </div>
                <button
                  type="button"
                  class="add-task-dialog__sign-off-toggle"
                  :class="{ 'add-task-dialog__sign-off-toggle--on': addTaskData.auto_approve }"
                  :aria-checked="addTaskData.auto_approve ? 'true' : 'false'"
                  :aria-label="autoApproveLabels.LABEL"
                  @click="onAutoApproveToggle"
                >
                  <span class="add-task-dialog__sign-off-toggle-thumb" />
                </button>
              </div>
              <p
                v-if="showAutoApproveNextSubmitHint"
                class="add-task-dialog__auto-approve-hint caption grey--text mb-0"
              >
                {{ autoApproveLabels.NEXT_SUBMIT_HINT }}
              </p>
            </div>
          </div>
          </div>
          <CycleTaskDependencySection
            v-model="dependencyChips"
            :expanded="dependenciesExpanded"
            :project-id="projectId"
            :subject-duedate="addTaskData.duedate || ''"
            :exclude-task-id="addTaskData._id || ''"
            :existing-neighbor-ids="dependencyNeighborIds"
            :waiting-on-neighbor-ids="dependencyWaitingOnIds"
            :waiting-on-neighbors="dependencyWaitingOnRows"
            @update:expanded="onDependenciesExpanded"
            @update:removedWaitingOn="removedWaitingOnIds = $event"
          />
        </div>

        <!-- AR: Details (two-pane) → Account (GLs) — no Assignment stepper step -->
        <div v-else class="add-task-dialog__step-body" data-testid="tasklist__add-task-dialog__ar-body">
          <!-- Step 1: Details + Assignment side-by-side (Figma) -->
          <div
            v-show="activeStep === ADD_TASK_STEPS.DETAILS"
            class="add-task-dialog__panes"
            data-testid="tasklist__add-task-dialog__details-step"
          >
            <div class="add-task-dialog__details-section">
              <div class="add-task-dialog__pane add-task-dialog__pane--left">
                <p class="add-task-dialog__section-label add-task-dialog__section-label--required">Details</p>

                <CollapsibleTextAreaField
                  v-model="addTaskData.description"
                  label="Task Name"
                  variant="plain"
                  :collapsible="false"
                  :expanded="true"
                  :required="true"
                  :rows="5"
                  :error="addTaskDataError.description"
                  data-testid="tasklist__add-task-dialog__task-name"
                  placeholder="e.g., Conduct Q3 Financial Audit"
                />

                <CollapsibleTextAreaField
                  v-model="addTaskData.purpose"
                  label="Purpose"
                  variant="card"
                  :expanded.sync="purposeExpanded"
                  :rows="3"
                  :error="addTaskDataError.purpose"
                  :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.PURPOSE"
                  data-testid="tasklist__add-task-dialog__purpose"
                />

                <CollapsibleTextAreaField
                  v-model="addTaskData.instruction"
                  label="Instruction"
                  variant="card"
                  :expanded.sync="instructionExpanded"
                  :rows="3"
                  :error="addTaskDataError.instruction"
                  :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.INSTRUCTION"
                  data-testid="tasklist__add-task-dialog__instruction"
                />

                <div class="add-task-dialog__details-block">
                  <div class="add-task-dialog__evidence-block">
                    <p class="add-task-dialog__field-label add-task-dialog__evidence-count-label">
                      {{ evidenceRequiredLabels.COUNT_LABEL }}
                    </p>
                    <div class="add-task-dialog__evidence-row">
                      <div class="add-task-dialog__evidence-count">
                        <v-text-field
                          v-model.number="addTaskData.no_of_evidence"
                          class="add-task-dialog__evidence-input"
                          type="number"
                          :min="evidenceRequiredLabels.COUNT_MIN"
                          :max="evidenceRequiredLabels.COUNT_MAX"
                          :step="1"
                          outlined
                          dense
                          hide-details="auto"
                          :error-messages="addTaskDataError.no_of_evidence"
                          data-testid="tasklist__add-task-dialog__no-of-evidences"
                          @input="onEvidenceInput"
                          @keypress="onEvidenceCountKeypress"
                          @paste="onEvidenceCountPaste"
                        />
                      </div>
                      <div class="add-task-dialog__evidence-required-cell">
                        <div class="add-task-dialog__evidence-required-group">
                          <p class="add-task-dialog__evidence-required-label">{{ evidenceRequiredLabels.LABEL }}</p>
                          <button
                            type="button"
                            class="add-task-dialog__sign-off-toggle"
                            :class="{ 'add-task-dialog__sign-off-toggle--on': addTaskData.evidence_required }"
                            :aria-checked="addTaskData.evidence_required ? 'true' : 'false'"
                            :aria-label="evidenceRequiredLabels.LABEL"
                            data-testid="tasklist__add-task-dialog__evidence-required-toggle"
                            @click="onEvidenceRequiredToggle"
                          >
                            <span class="add-task-dialog__sign-off-toggle-thumb" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="add-task-dialog__row">
                    <div class="add-task-dialog__row-item">
                      <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--primary">Category</p>
                      <lb-dropdown
                        :key="`category-ar-${addTaskData.task_type || 'none'}`"
                        v-model="addTaskData.category"
                        :multiple="false"
                        :items="categoryItemsForTaskType"
                        :customvalue="false"
                        :searchable="true"
                        appendiconinner="mdi-chevron-down"
                        :attach="false"
                        :clearable="false"
                        :error="addTaskDataError.category"
                        label=""
                        placeholder="Select"
                        data-testid="tasklist__add-task-dialog__category"
                      />
                    </div>
                    <div class="add-task-dialog__row-item">
                      <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--primary">Sub Category</p>
                      <lb-dropdown
                        :key="`subcategory-ar-${addTaskData.task_type || 'none'}`"
                        v-model="addTaskData.subcategory"
                        :multiple="false"
                        :items="subcategoryItemsForTaskType"
                        :customvalue="false"
                        :searchable="true"
                        appendiconinner="mdi-chevron-down"
                        :attach="false"
                        :clearable="false"
                        :error="addTaskDataError.subcategory"
                        label=""
                        placeholder="Select"
                        data-testid="tasklist__add-task-dialog__subcategory"
                      />
                    </div>
                  </div>

                  <div class="add-task-dialog__row">
                    <div class="add-task-dialog__row-item">
                      <p class="add-task-dialog__field-label">Assertions</p>
                      <lb-tagsInput
                        v-model="addTaskData.assertions"
                        class="add-task-dialog__tags-input"
                        :hidedetails="true"
                        :items="assertionItems"
                        label=""
                        :error="addTaskDataError.assertions"
                        tagcolor="purpleborder"
                        :multiple="true"
                        :multiplechips="true"
                        :allow-overflow="true"
                        :menu-search="true"
                        :menu-offset-y="false"
                        menu-content-class="add-task-dialog__tags-menu"
                        search-placeholder="Search"
                        appendiconinner="mdi-chevron-down"
                        data-testid="tasklist__add-task-dialog__assertions"
                      />
                    </div>
                  </div>
                  <div v-if="showEditTaskTypeField" class="add-task-dialog__row">
                    <div class="add-task-dialog__row-item">
                      <p class="add-task-dialog__field-label">Task Type</p>
                      <lb-dropdown
                        v-if="isEditTaskTypeEditable"
                        v-model="addTaskData.task_type"
                        :multiple="false"
                        :items="editTaskTypeOptions"
                        itemtext="name"
                        itemvalue="value"
                        appendiconinner="mdi-chevron-down"
                        :clearable="false"
                        :searchable="false"
                        label=""
                        placeholder="Select"
                        :error="addTaskDataError.task_type"
                        data-testid="tasklist__add-task-dialog__task-type"
                      />
                      <v-text-field
                        v-else
                        :value="taskTypeDisplayLabel"
                        outlined
                        dense
                        readonly
                        disabled
                        hide-details
                        data-testid="tasklist__add-task-dialog__task-type"
                      />
                    </div>
                  </div>

                  <div
                    v-if="resolvedCustomColumns.length > 0"
                    class="add-task-dialog__custom-grid"
                  >
                    <div
                      v-for="field in resolvedCustomColumns"
                      :key="`ar-${field.columnName}`"
                      class="add-task-dialog__row-item"
                    >
                      <p class="add-task-dialog__field-label add-task-dialog__field-label--primary">{{ field.displayText }}</p>
                      <lb-string
                        v-if="field.dataType === 'string'"
                        v-model="addTaskData[field.columnName]"
                        label=""
                        :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                        :error="addTaskDataError[field.columnName]"
                        :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                      />
                      <lb-date
                        v-else-if="field.dataType === 'date'"
                        v-model="addTaskData[field.columnName]"
                        label=""
                        :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                        :error="addTaskDataError[field.columnName]"
                        :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                      />
                      <lb-number
                        v-else-if="field.dataType === 'number'"
                        v-model="addTaskData[field.columnName]"
                        allow-empty
                        label=""
                        :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                        :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                      />
                      <lb-textBox
                        v-else
                        v-model="addTaskData[field.columnName]"
                        label=""
                        :placeholder="ADD_TASK_FIELD_PLACEHOLDERS.CUSTOM_FIELD"
                        :data-testid="`tasklist__add-task-dialog__custom-field--${field.columnName}`"
                      />
                    </div>
                  </div>
                </div>

                <div class="add-task-dialog__schedule">
                  <p class="add-task-dialog__section-label">Schedule</p>
                  <div>
                    <p class="add-task-dialog__field-label add-task-dialog__field-label--required">Due Date</p>
                    <AddTaskDueDateField
                      v-model="addTaskData.duedate"
                      :error="addTaskDataError.duedate"
                      :min="dueDateMinDate"
                      :max="dueDateMaxDate"
                      :blocked-due-before="blockedDueBefore"
                      :invalid-date-tooltip="calendarBlockedDueTooltip"
                      placeholder="Select Due Date"
                      data-testid="tasklist__add-task-dialog__due-date"
                    />
                  </div>
                  <div class="add-task-dialog__priority">
                    <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--bold">Priority</p>
                    <TaskPriorityDisplay
                      variant="toggle-segment"
                      :value="addTaskData.priority"
                      data-testid="tasklist__add-task-dialog__priority"
                      @input="addTaskData.priority = $event"
                    />
                    <span v-if="addTaskDataError.priority" class="caption error--text">
                      {{ formatError(addTaskDataError.priority) }}
                    </span>
                  </div>
                  <div class="add-task-dialog__tags">
                    <p class="add-task-dialog__field-label">Frequency</p>
                    <lb-tagsInput
                      v-model="addTaskData.tags"
                      class="add-task-dialog__tags-input"
                      tagcolor="purpleborder"
                      :multiple="true"
                      :multiplechips="true"
                      :items="allTags"
                      :hidedetails="true"
                      :menu-search="true"
                      :menu-offset-y="false"
                      menu-content-class="add-task-dialog__tags-menu"
                      search-placeholder="Search"
                      empty-message="No frequency found"
                      appendiconinner="mdi-chevron-down"
                      label=""
                      placeholder=""
                      :error="addTaskDataError.tags"
                      data-testid="tasklist__add-task-dialog__tags"
                    />
                  </div>
                </div>
              </div>

              <div class="add-task-dialog__pane-divider" role="separator"></div>

              <div class="add-task-dialog__pane add-task-dialog__pane--right">
                <p class="add-task-dialog__section-label">Assignment</p>

                <div :class="{ 'add-task-dialog__location-error': !!addTaskDataError.location }">
                  <p class="add-task-dialog__field-label add-task-dialog__field-label--required add-task-dialog__field-label--primary">{{ locationHeader }}</p>
                  <ChipMultiSelect
                    v-model="addTaskData.location"
                    :single="true"
                    :clearable="true"
                    variant="add-task"
                    :items="actorCycleLocationList"
                    item-text="name"
                    item-value="_id"
                    :searchable="true"
                    search-placeholder="Search"
                    :error="addTaskDataError.location"
                    placeholder="Select"
                    data-testid="tasklist__add-task-dialog__location"
                    data-testid-prefix="tasklist__add-task-dialog__location"
                  />
                </div>

                <div :class="{ 'add-task-dialog__field-error': !!addTaskDataError.preparer }">
                  <UserChipMultiSelect
                    :value="addTaskData.preparer"
                    variant="add-task"
                    label="Preparer(s)"
                    :required="true"
                    :users="preparerPickerUsers"
                    :allowed-menu-ids="allowedUserIds"
                    :exclude-ids="preparerExcludeIds"
                    :loading="usersFetchLoading"
                    role-label="preparer"
                    placeholder="Select"
                    :empty-message="assignmentUsersEmptyMessage"
                    :attach="true"
                    data-testid="tasklist__add-task-dialog__preparer"
                    @input="onAssignmentRoleChange('preparer', $event)"
                  />
                  <span v-if="addTaskDataError.preparer" class="caption error--text">
                    {{ formatError(addTaskDataError.preparer) }}
                  </span>
                </div>

                <div :class="{ 'add-task-dialog__field-error': !!(addTaskDataError.approver || addTaskDataError.location_users) }">
                  <UserChipMultiSelect
                    :value="addTaskData.approver"
                    variant="add-task"
                    label="Approver(s)"
                    :required="true"
                    :users="approverPickerUsers"
                    :allowed-menu-ids="allowedUserIds"
                    :exclude-ids="approverExcludeIds"
                    :loading="usersFetchLoading"
                    role-label="approver"
                    placeholder="Select"
                    :empty-message="assignmentUsersEmptyMessage"
                    :attach="true"
                    data-testid="tasklist__add-task-dialog__approver"
                    @input="onAssignmentRoleChange('approver', $event)"
                  />
                  <span v-if="addTaskDataError.approver || addTaskDataError.location_users" class="caption error--text">
                    {{ formatError(addTaskDataError.approver || addTaskDataError.location_users) }}
                  </span>
                </div>

                <div class="add-task-dialog__sign-off-section add-task-dialog__sign-off-edit-allowed">
                  <div class="add-task-dialog__sign-off" data-testid="tasklist__add-task-sign-off-stub">
                    <div class="add-task-dialog__sign-off-copy">
                      <p class="add-task-dialog__sign-off-title">{{ signOffLabels.REQUIRES_LABEL }}</p>
                      <p class="add-task-dialog__sign-off-desc">{{ signOffLabels.REQUIRES_DESC }}</p>
                    </div>
                    <button
                      type="button"
                      class="add-task-dialog__sign-off-toggle"
                      :class="{ 'add-task-dialog__sign-off-toggle--on': addTaskData.requires_sign_off }"
                      :aria-checked="addTaskData.requires_sign_off ? 'true' : 'false'"
                      :aria-label="signOffLabels.REQUIRES_LABEL"
                      data-testid="tasklist__add-task-dialog__requires-sign-off"
                      @click="onRequiresSignOffToggle"
                    >
                      <span class="add-task-dialog__sign-off-toggle-thumb" />
                    </button>
                  </div>
                  <template v-if="addTaskData.requires_sign_off">
                    <div class="add-task-dialog__sign-off-picker">
                      <p class="add-task-dialog__sign-off-picker-label">{{ signOffLabels.APPROVERS_LABEL }}</p>
                      <p class="add-task-dialog__sign-off-hint ma-0">{{ signOffLabels.PICKER_HINT }}</p>
                      <UserChipMultiSelect
                        :value="addTaskData.sign_off_approvers"
                        variant="add-task"
                        label=""
                        :users="signOffPickerUsers"
                        :allowed-menu-ids="allowedUserIds"
                        :exclude-ids="signOffExcludeIds"
                        :loading="usersFetchLoading"
                        role-label="sign-off approver"
                        placeholder="Select"
                        :attach="true"
                        data-testid="tasklist__add-task-dialog__sign-off-approver"
                        @input="onAssignmentRoleChange('sign_off_approvers', $event)"
                      />
                      <span v-if="addTaskDataError.sign_off_approvers" class="caption error--text">
                        {{ formatError(addTaskDataError.sign_off_approvers) }}
                      </span>
                    </div>
                  </template>
                </div>

                <div class="add-task-dialog__sign-off-section">
                  <div class="add-task-dialog__sign-off" data-testid="tasklist__add-task-dialog__enforce-threshold">
                    <div class="add-task-dialog__sign-off-copy">
                      <p class="add-task-dialog__sign-off-title">Enforce Threshold</p>
                      <p class="add-task-dialog__sign-off-desc">
                        {{ locationHeader }} specific threshold applies and prevents approval until all differences above the limit are explained.
                      </p>
                    </div>
                    <button
                      type="button"
                      class="add-task-dialog__sign-off-toggle"
                      :class="{ 'add-task-dialog__sign-off-toggle--on': isEnforceThreshold }"
                      :aria-checked="isEnforceThreshold ? 'true' : 'false'"
                      aria-label="Enforce Threshold"
                      data-testid="tasklist__add-task-dialog__enforce-threshold-toggle"
                      @click="onEnforceThresholdToggle"
                    >
                      <span class="add-task-dialog__sign-off-toggle-thumb" />
                    </button>
                  </div>
                </div>

                <div class="add-task-dialog__sign-off-section">
                  <div class="add-task-dialog__sign-off" data-testid="tasklist__add-task-dialog__auto-approve">
                    <div class="add-task-dialog__sign-off-copy">
                      <p class="add-task-dialog__sign-off-title">{{ autoApproveLabels.LABEL }}</p>
                      <p class="add-task-dialog__sign-off-desc">{{ autoApproveDescription }}</p>
                    </div>
                    <button
                      type="button"
                      class="add-task-dialog__sign-off-toggle"
                      :class="{ 'add-task-dialog__sign-off-toggle--on': addTaskData.auto_approve }"
                      :aria-checked="addTaskData.auto_approve ? 'true' : 'false'"
                      :aria-label="autoApproveLabels.LABEL"
                      @click="onAutoApproveToggle"
                    >
                      <span class="add-task-dialog__sign-off-toggle-thumb" />
                    </button>
                  </div>
                  <p
                    v-if="showAutoApproveNextSubmitHint"
                    class="add-task-dialog__auto-approve-hint caption grey--text mb-0"
                  >
                    {{ autoApproveLabels.NEXT_SUBMIT_HINT }}
                  </p>
                </div>
              </div>
            </div>
            <CycleTaskDependencySection
              v-model="dependencyChips"
              :expanded="dependenciesExpanded"
              :project-id="projectId"
              :subject-duedate="addTaskData.duedate || ''"
              :exclude-task-id="addTaskData._id || ''"
              :existing-neighbor-ids="dependencyNeighborIds"
              :waiting-on-neighbor-ids="dependencyWaitingOnIds"
            :waiting-on-neighbors="dependencyWaitingOnRows"
              @update:expanded="onDependenciesExpanded"
              @update:removedWaitingOn="removedWaitingOnIds = $event"
            />
          </div>

          <!-- Step 2: Account — GL selection only -->
          <div
            v-show="activeStep === ADD_TASK_STEPS.ACCOUNTS"
            class="add-task-dialog__accounts-step"
            data-testid="tasklist__add-task-dialog__accounts-step"
          >
            <div
              v-if="addTaskData.location"
              class="add-task-dialog__gl-selection add-task-dialog__gl-selection--step"
            >
              <TaskGLSelection
                variant="assign-accounts"
                :addeditdata="addTaskData"
                :addeditdataerror="addTaskDataError"
                :selectgls.sync="selectGls"
                :searchcustomfilter.sync="searchCustomFilter"
                :hide-update-button="true"
                :locations="cycleLocationCatalog"
                data-testid="tasklist__add-task-dialog__gl-selection"
              />
            </div>
            <p
              v-else
              class="add-task-dialog__hint"
              data-testid="tasklist__add-task-dialog__gl-required-hint"
            >
              Select a location on the Details step before choosing GL accounts.
            </p>
            <span v-if="addTaskDataError.glcode" class="caption error--text">
              {{ formatError(addTaskDataError.glcode) }}
            </span>
          </div>
        </div>
      </div>

      <div class="add-task-dialog__footer">
        <v-btn
          class="secondaryBtn"
          data-testid="tasklist__add-task-dialog__cancel-btn"
          @click="close"
        >
          Cancel
        </v-btn>
        <template v-if="isAccountReconciliation">
          <v-btn
            v-if="!isFirstStep"
            class="secondaryBtn"
            data-testid="tasklist__add-task-dialog__previous-btn"
            @click="goPrevious"
          >
            Previous
          </v-btn>
          <v-btn
            v-if="!isLastStep"
            class="primary"
            :loading="accessValidateLoading"
            :disabled="accessValidateLoading"
            data-testid="tasklist__add-task-dialog__next-btn"
            @click="goNext"
          >
            Next
          </v-btn>
          <v-tooltip v-else top :disabled="isFormValid || loading" content-class="tooltip-top">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  class="primary"
                  :loading="loading"
                  :disabled="!isFormValid || loading"
                  data-testid="tasklist__add-task-dialog__save-btn"
                  @click="onSave"
                >
                  Save
                </v-btn>
              </span>
            </template>
            <span>Fill all required fields</span>
          </v-tooltip>
        </template>

        <v-btn
          v-else
          class="primary"
          :loading="loading"
          :disabled="loading"
          data-testid="tasklist__add-task-dialog__save-btn"
          @click="onSave"
        >
          Save
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import './AddTaskDialog.scss';
import { getTaskTypeDisplay, getTaskTypeOptions } from '../../../../../utils/utils';
import {
  MAX_DUE_DATE_DAYS,
  SIGN_OFF,
  AUTO_APPROVE,
  EVIDENCE_REQUIRED,
  coerceEvidenceRequired,
  USER_ROLE,
  APPROVER_TAG,
} from '../../../../../utils/constants';
import { getCycleDueDateWindow } from '../../../../../utils/workingDays';
import {
  syncAssignmentRolesOnChange,
  normalizeUserIdList,
  excludeIdsForAssignmentRole,
  withCycleUserRoles,
  activeUserIdsFromUserRoles,
  buildCycleUserRolesFromAssignments,
} from '../../../../Setup/TaskList/utils/assignmentRoles';
import { CollapsibleTextAreaField, UserChipMultiSelect, FreeformSelect, ChipMultiSelect } from '../../../../../components';
import TaskPriorityDisplay from '../../components/TaskPriorityDisplay/TaskPriorityDisplay.vue';
import TaskGLSelection from '../../../components/TaskGLSelection.vue';
import AddTaskDueDateField from './AddTaskDueDateField.vue';
import CycleTaskDependencySection from './CycleTaskDependencySection.vue';
import {
  createEmptyAddTaskData,
  validateTaskFields,
  validateTaskStep,
  getStepValidationKeys,
  resolveEvidenceFieldState,
  isEvidenceCountKeyAllowed,
  parsePastedEvidenceCount,
  ensureCurrentValueInItems,
  toOptionStrings,
  hydrateSignOffFromTask,
  isL2ClosedTask,
  normalizeTaskTypeForForm,
  buildAssignmentUserOptions,
  buildKnownUserMap,
  filterAssignmentIdsToKnownUsers,
  inactiveAssignmentIdSet,
  mergeSelectedAssignmentPickerUsers,
  formHasInactiveAssignmentUsers,
  ADD_TASK_STEPS,
  ADD_TASK_FIELD_PLACEHOLDERS,
  ADD_TASK_ASSIGNMENT_EMPTY_MESSAGES,
} from './addTaskDialogUtils';
import { isChecklistTask, normalizeEnforceThresholdForTaskType } from '../../../../../utils/tasklistHelpers';
import {
  fetchUsersForLocations,
  filterUsersToAllowedIds,
  dropIdsNotAllowed,
} from '../../../../../utils/locationUserAccess';
import {
  chipsHaveMissingLinkType,
  cycleChipsFromDetailArrays,
  cycleNeighborIdsFromDetail,
  cycleWaitingOnIdsFromDetail,
  linksPayloadFromChips,
  earliestBlockedDueLimit,
} from '../../../../Setup/TaskList/utils/setupDependencyUtils';
import { CYCLE_TASK_DEPENDENCY } from '../../../../../utils/constants';
import { glPayloadFromSelection } from '../../../../../utils/taskGlPayload';
import { mergeLocationTypes } from '../../../../../utils/locationMasterType';

/** Add-only task dialog — Checklist two-pane / AR stepper per Figma. */
export default {
  name: 'AddTaskDialog',

  components: {
    CollapsibleTextAreaField,
    UserChipMultiSelect,
    FreeformSelect,
    ChipMultiSelect,
    TaskPriorityDisplay,
    AddTaskDueDateField,
    CycleTaskDependencySection,
    TaskGLSelection,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: String,
      default: '',
    },
    customColumns: {
      type: Array,
      default: () => [],
    },
    categoryAcc: {
      type: Array,
      default: () => [],
    },
    subcategoryAcc: {
      type: Array,
      default: () => [],
    },
    coaCategoryAcc: {
      type: Array,
      default: () => [],
    },
    coaSubcategoryAcc: {
      type: Array,
      default: () => [],
    },
    locationList: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    dueDateMinDate: {
      type: String,
      default: '',
    },
    dueDateMaxDate: {
      type: String,
      default: '',
    },
    locationHeader: {
      type: String,
      default: 'Location',
    },
    allTags: {
      type: Array,
      default: () => [],
    },
    possibleAssertions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ADD_TASK_STEPS,
      ADD_TASK_FIELD_PLACEHOLDERS,
      addTaskData: createEmptyAddTaskData(),
      addTaskDataError: {},
      selectGls: [],
      searchCustomFilter: '',
      purposeExpanded: false,
      instructionExpanded: false,
      maxDueDateDays: MAX_DUE_DATE_DAYS,
      selectedTaskType: '',
      originalTaskType: '',
      activeStep: ADD_TASK_STEPS.DETAILS,
      allowedUserIds: [],
      usersFetchLoading: false,
      usersFetchToken: 0,
      accessValidateLoading: false,
      dependencyChips: [],
      dependenciesExpanded: false,
      dependenciesExpandLocked: false,
      dependencyNeighborIds: [],
      dependencyWaitingOnIds: [],
      dependencyWaitingOnRows: [],
      removedWaitingOnIds: [],
    };
  },

  computed: {
    TASK_TYPES() {
      return this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES;
    },

    /** Full assertion list. Callers may omit the prop; already-selected values stay in the menu. */
    assertionItems() {
      const fromProp = Array.isArray(this.possibleAssertions) ? this.possibleAssertions : [];
      if (fromProp.length) return fromProp;
      return this.$constants.TASK_LIST_CONSTANTS.ASSERTIONS || [];
    },

    isEditMode() {
      return !!(this.addTaskData?._id);
    },

    blockedDueBefore() {
      return earliestBlockedDueLimit(this.dependencyChips, CYCLE_TASK_DEPENDENCY.MODE) || '';
    },

    calendarBlockedDueTooltip() {
      return this.blockedDueBefore ? CYCLE_TASK_DEPENDENCY.CALENDAR_BLOCKED_DUE_TOOLTIP : '';
    },

    /** True when cycle end metadata drives the due-date window (not today + max days fallback). */
    dueDateValidationUsesCycleEnd() {
      const cycleEnd = this.$store.getters['tasks/getTaskListData']?.metadata?.to;
      return getCycleDueDateWindow(cycleEnd, MAX_DUE_DATE_DAYS).usesCycleEnd;
    },

    taskTypeDisplayLabel() {
      const label = getTaskTypeDisplay(this.addTaskData.task_type);
      return label || '—';
    },

    showEditTaskTypeField() {
      return this.isEditMode && !this.signOffOnlyEditMode;
    },

    isEditTaskTypeEditable() {
      if (!this.showEditTaskTypeField) return false;
      const original = normalizeTaskTypeForForm(this.originalTaskType, this.TASK_TYPES);
      return original !== this.TASK_TYPES.ACCOUNT_RECONCILIATION;
    },

    editTaskTypeOptions() {
      return getTaskTypeOptions();
    },

    dialogTitle() {
      const action = this.isEditMode ? 'Edit' : 'Add';
      if (this.isAccountReconciliation) {
        return `${action} Account Reconciliation Task`;
      }
      const typeLabel = getTaskTypeDisplay(this.addTaskData.task_type || this.selectedTaskType);
      return typeLabel ? `${action} ${typeLabel}` : `${action} Task`;
    },

    isChecklistTaskType() {
      const type = this.addTaskData?.task_type || this.selectedTaskType;
      if (isChecklistTask(type)) return true;
      return type !== this.TASK_TYPES.ACCOUNT_RECONCILIATION;
    },

    isAccountReconciliation() {
      return !this.isChecklistTaskType;
    },

    arSteps() {
      return [
        { id: ADD_TASK_STEPS.DETAILS, label: 'Details' },
        { id: ADD_TASK_STEPS.ACCOUNTS, label: 'Account' },
      ];
    },

    stepOrder() {
      return this.arSteps.map((s) => s.id);
    },

    isFirstStep() {
      return this.activeStep === this.stepOrder[0];
    },

    isLastStep() {
      return this.activeStep === this.stepOrder[this.stepOrder.length - 1];
    },

    checklistCategoryOptions() {
      return toOptionStrings(this.categoryAcc);
    },

    checklistSubcategoryOptions() {
      return toOptionStrings(this.subcategoryAcc);
    },

    coaCategoryOptions() {
      return toOptionStrings(this.coaCategoryAcc);
    },

    coaSubcategoryOptions() {
      return toOptionStrings(this.coaSubcategoryAcc);
    },

    categoryItemsForTaskType() {
      if (this.isAccountReconciliation) {
        return ensureCurrentValueInItems(this.coaCategoryOptions, this.addTaskData?.category);
      }
      // Checklist allows custom values; do not rebuild items from the current
      // selection or object-shaped master rows (that locks the combobox).
      return this.checklistCategoryOptions;
    },

    subcategoryItemsForTaskType() {
      if (this.isAccountReconciliation) {
        return ensureCurrentValueInItems(this.coaSubcategoryOptions, this.addTaskData?.subcategory);
      }
      return this.checklistSubcategoryOptions;
    },

    signOffLabels() {
      return SIGN_OFF;
    },

    autoApproveLabels() {
      return AUTO_APPROVE;
    },

    evidenceRequiredLabels() {
      return EVIDENCE_REQUIRED;
    },

    autoApproveDescription() {
      return this.isAccountReconciliation ? AUTO_APPROVE.DESC_AR : AUTO_APPROVE.DESC_CHECKLIST;
    },

    isEnforceThreshold() {
      return this.addTaskData.enforceThreshold !== false;
    },

    showAutoApproveNextSubmitHint() {
      if (!this.isEditMode || !this.addTaskData.auto_approve) return false;
      if (Number(this.addTaskData.stage) !== 2) return false;
      return this.addTaskData.approval_state?.l1_completed !== true;
    },

    allKnownUsers() {
      const byId = new Map();
      const addUsers = (list) => {
        (Array.isArray(list) ? list : []).forEach((user) => {
          const id = user?._id || user?.id;
          if (!id) return;
          byId.set(String(id), user);
        });
      };
      addUsers(this.$store.getters['tasks/getFormDataV3']?.users);
      addUsers(Object.values(this.$store.getters['tasks/getUserMap'] || {}));
      addUsers(this.$store.getters['tasks/getActiveUsersList']);
      addUsers(this.$store.getters['tasks/getInactiveUsersList']);
      addUsers(this.users);
      return Array.from(byId.values());
    },

    knownUserIdSet() {
      return new Set(this.allKnownUsers.map((user) => String(user._id || user.id)).filter(Boolean));
    },

    activeAssignmentIdSet() {
      const set = new Set();
      (this.users || []).forEach((user) => {
        const id = user?._id || user?.id;
        if (id) set.add(String(id));
      });
      (this.$store.getters['tasks/getActiveUsersIds'] || []).forEach((id) => {
        if (id) set.add(String(id));
      });
      this.allKnownUsers.forEach((user) => {
        if (user && user.isActive === true) {
          const id = user._id || user.id;
          if (id) set.add(String(id));
        }
      });
      return set;
    },

    selectedAssignmentIds() {
      return [
        ...normalizeUserIdList(this.addTaskData.preparer),
        ...normalizeUserIdList(this.addTaskData.approver),
        ...normalizeUserIdList(this.addTaskData.sign_off_approvers),
      ];
    },

    /**
     * Cycle location rows plus formdata `type` so Assign Accounts can detect
     * Consolidated without a second location fetch.
     */
    cycleLocationCatalog() {
      const form = this.$store.getters['tasks/getFormDataV3'] || {};
      return mergeLocationTypes(this.locationList || [], [
        ...(form.locations || []),
        ...(form.actorLocations || []),
      ]);
    },
    /**
     * Location options: v3 getFormData actorLocations ∩ cycle locationList prop.
     * Full `locations` catalog is for name resolution only — not for this picker.
     */
    actorCycleLocationList() {
      const formLocs = this.$store.getters['tasks/getFormDataV3']?.actorLocations || [];
      const formIds = new Set(
        formLocs.map((loc) => String(loc?._id || loc?.id || '')).filter(Boolean),
      );
      return (this.locationList || []).filter((loc) => {
        const id = String(loc?._id || loc?.id || '');
        return id && formIds.has(id);
      });
    },

    assignmentUsers() {
      return buildAssignmentUserOptions({
        activeUsers: this.users,
        allUsers: this.allKnownUsers,
        selectedIds: this.selectedAssignmentIds,
        isEdit: this.isEditMode,
        activeIdSet: this.activeAssignmentIdSet,
      });
    },

    /** Parent-filtered users for preparer/approver/sign-off (usersForLocations). */
    locationScopedUsers() {
      return filterUsersToAllowedIds(this.assignmentUsers, this.allowedUserIds);
    },

    knownUserMap() {
      return buildKnownUserMap([this.allKnownUsers, this.users, this.assignmentUsers]);
    },

    preparerPickerUsers() {
      return mergeSelectedAssignmentPickerUsers(
        this.locationScopedUsers,
        this.addTaskData.preparer,
        this.knownUserMap,
      );
    },

    approverPickerUsers() {
      return mergeSelectedAssignmentPickerUsers(
        this.locationScopedUsers,
        this.addTaskData.approver,
        this.knownUserMap,
      );
    },

    signOffPickerUsers() {
      return mergeSelectedAssignmentPickerUsers(
        this.signOffApproverUsers,
        this.addTaskData.sign_off_approvers,
        this.knownUserMap,
      );
    },

    /** Refetch usersForLocations only when the selected location id changes. */
    locationIdKey() {
      const id = this.addTaskData?.location;
      return id ? String(id) : '';
    },

    assignmentUsersEmptyMessage() {
      return this.addTaskData.location
        ? ADD_TASK_ASSIGNMENT_EMPTY_MESSAGES.NO_USERS_FOUND
        : ADD_TASK_ASSIGNMENT_EMPTY_MESSAGES.SELECT_LOCATION_BEFORE_USERS;
    },

    inactiveUserIds() {
      return inactiveAssignmentIdSet(this.locationScopedUsers);
    },

    signOffApproverUsers() {
      const blocked = new Set(this.signOffExcludeIds);
      return (this.locationScopedUsers || []).filter((user) => !blocked.has(String(user._id)));
    },

    preparerExcludeIds() {
      return excludeIdsForAssignmentRole(this.addTaskData, USER_ROLE.PREPARER);
    },

    approverExcludeIds() {
      return excludeIdsForAssignmentRole(this.addTaskData, USER_ROLE.APPROVER);
    },

    signOffExcludeIds() {
      return excludeIdsForAssignmentRole(this.addTaskData, USER_ROLE.SIGN_OFF_APPROVERS);
    },

    resolvedCustomColumns() {
      return (this.customColumns || [])
        .filter((field) => field?.columnName && !field.isSystem)
        .map((field) => ({
          ...field,
          displayText: field.displayText || field.displayName || field.label || field.columnName,
        }));
    },

    validationContext() {
      return {
        customColumns: this.resolvedCustomColumns,
        selectGls: this.selectGls,
        coaCategoryAcc: this.coaCategoryAcc,
        coaSubcategoryAcc: this.coaSubcategoryAcc,
        dueDateMinDate: this.dueDateMinDate,
        dueDateMaxDate: this.dueDateMaxDate,
        dueDateUsesCycleEnd: this.dueDateValidationUsesCycleEnd,
        maxDueDateDays: this.maxDueDateDays,
        errorMessages: this.$constants.TASK_LIST_CONSTANTS.ERROR_MESSAGES,
        taskTypes: this.TASK_TYPES,
        users: this.locationScopedUsers,
        inactiveUserIds: this.inactiveUserIds,
      };
    },

    isFormValid() {
      return validateTaskFields(this.addTaskData, this.validationContext).valid;
    },

    assignmentFieldError() {
      const err = this.addTaskDataError || {};
      return !!(err.location || err.preparer || err.approver || err.location_users);
    },
  },

  watch: {
    visible(val) {
      if (!val) {
        this.selectedTaskType = '';
        this.resetForm();
      } else {
        this.$nextTick(() => this.resetBodyScroll());
      }
    },

    'addTaskData.location'(newLocation, oldLocation) {
      if (newLocation && oldLocation && newLocation !== oldLocation) {
        this.clearGlSelection();
      }
    },

    locationIdKey() {
      this.refreshAllowedUsersForLocation();
    },

    actorCycleLocationList: {
      handler(list) {
        const allowed = new Set((list || []).map((loc) => String(loc?._id || loc?.id || '')).filter(Boolean));
        const selected = this.addTaskData?.location;
        if (selected && allowed.size && !allowed.has(String(selected))) {
          const kept = dropIdsNotAllowed([selected], [...allowed]);
          this.addTaskData.location = kept[0] || '';
        }
      },
      deep: true,
    },

    'addTaskData.task_type'(newType, oldType) {
      if (!this.isEditTaskTypeEditable || !oldType || newType === oldType) return;
      this.onTaskTypeChange(newType, oldType);
    },
  },

  beforeDestroy() {
    this.selectedTaskType = '';
    this.resetForm();
  },

  methods: {
    normalizeAssignmentId(id) {
      if (id === undefined || id === null || id === '') return '';
      if (typeof id === 'object' && id._id != null) return String(id._id);
      return String(id);
    },

    applyDependenciesExpandedDefault() {
      if (this.dependenciesExpandLocked) return;
      const hasWaitingOn = (this.dependencyWaitingOnIds || []).length > 0
        || (this.dependencyWaitingOnRows || []).length > 0;
      this.dependenciesExpanded = this.dependencyChips.length > 0 || hasWaitingOn;
    },

    onDependenciesExpanded(expanded) {
      this.dependenciesExpandLocked = true;
      this.dependenciesExpanded = expanded;
    },

    resetBodyScroll() {
      const body = this.$refs.dialogBody;
      if (body) body.scrollTop = 0;
    },

    open(taskType) {
      this.selectedTaskType = taskType || '';
      this.resetForm();
      this.$emit('update:visible', true);
      this.$nextTick(() => this.resetBodyScroll());
    },

    /** Open dialog pre-populated for editing an existing task. */
    async openForEdit(task) {
      this.resetForm();
      if (!task || !task._id) {
        this.open();
        return;
      }
      if (isL2ClosedTask(task)) {
        this.$store.commit('sbError', 'This task cannot be edited after Sign-Off is complete.', { root: true });
        return;
      }
      const signOff = hydrateSignOffFromTask(task);
      const knownIds = this.knownUserIdSet;
      const hasUserRoles = Array.isArray(task.user_roles) && task.user_roles.length > 0;
      const preparerSource = hasUserRoles
        ? activeUserIdsFromUserRoles(task.user_roles, USER_ROLE.PREPARER)
        : task.preparer;
      const approverSource = hasUserRoles
        ? activeUserIdsFromUserRoles(task.user_roles, USER_ROLE.APPROVER, APPROVER_TAG.L1)
        : task.approver;
      const preparer = filterAssignmentIdsToKnownUsers(preparerSource, knownIds);
      const approver = filterAssignmentIdsToKnownUsers(approverSource, knownIds);
      const signOffApprovers = filterAssignmentIdsToKnownUsers(signOff.sign_off_approvers, knownIds);
      const normalizedTaskType = normalizeTaskTypeForForm(task.task_type, this.TASK_TYPES);
      this.selectedTaskType = normalizedTaskType;
      this.originalTaskType = normalizedTaskType;
      this.addTaskData = {
        ...createEmptyAddTaskData(),
        ...task,
        task_type: normalizedTaskType,
        auto_approve: !!task.auto_approve,
        evidence_required: coerceEvidenceRequired(task.evidence_required),
        preparer,
        approver,
        requires_sign_off: signOff.requires_sign_off,
        sign_off_approvers: signOff.requires_sign_off ? signOffApprovers : [],
        user_roles: buildCycleUserRolesFromAssignments({
          preparer,
          approver,
          sign_off_approvers: signOff.requires_sign_off ? signOffApprovers : [],
        }),
        tags: Array.isArray(task.tags) ? [...task.tags] : [],
        assertions: Array.isArray(task.assertions) ? [...task.assertions] : [],
        no_of_evidence: task.no_of_evidence ?? task.evidences ?? 0,
        __tblinks: Array.isArray(task.__tblinks) ? [...task.__tblinks] : [],
        __tbtotals: task.__tbtotals ? { ...task.__tbtotals } : { items: [] },
      };
      this.activeStep = ADD_TASK_STEPS.DETAILS;
      this.selectGls = Array.isArray(task.__tblinks) ? [...task.__tblinks] : [];
      this.purposeExpanded = !!(task.purpose && String(task.purpose).trim());
      this.instructionExpanded = !!(task.instruction && String(task.instruction).trim());
      (this.customColumns || []).forEach((col) => {
        if (task[col.columnName] !== undefined) {
          this.$set(this.addTaskData, col.columnName, task[col.columnName]);
        }
      });
      await this.seedDependencyChipsForEdit(task);
      this.$emit('update:visible', true);
      this.$nextTick(() => {
        this.resetBodyScroll();
        this.refreshAllowedUsersForLocation();
      });
    },

    async seedDependencyChipsForEdit(task) {
      let source = task;
      const hasDetailArrays =
        Array.isArray(task?.blocking)
        && Array.isArray(task?.relates)
        && Array.isArray(task?.blockedBy);
      if (!hasDetailArrays) {
        const projectId =
          this.projectId
          || task.project
          || this.$route.params.id
          || this.$route.params.projectId
          || '';
        if (projectId && task._id) {
          try {
            const detail = await this.$store.dispatch('tasks/fetchTaskDependencyDetail', {
              projectId,
              taskId: task._id,
            });
            source = { ...task, ...detail };
          } catch (err) {
            console.error('[AddTaskDialog]: seedDependencyChipsForEdit', err);
          }
        }
      }
      this.dependencyChips = cycleChipsFromDetailArrays(source);
      this.dependencyNeighborIds = cycleNeighborIdsFromDetail(source);
      this.dependencyWaitingOnIds = cycleWaitingOnIdsFromDetail(source);
      this.dependencyWaitingOnRows = Array.isArray(source.blockedBy) ? source.blockedBy.slice() : [];
      this.applyDependenciesExpandedDefault();
    },

    resetForm() {
      this.signOffOnlyEditMode = false;
      this.originalTaskType = '';
      this.addTaskData = createEmptyAddTaskData();
      this.addTaskData.task_type = this.selectedTaskType || '';
      this.addTaskData.project = this.projectId || this.$route.params.id || this.$route.params.projectId || '';
      this.addTaskDataError = {};
      this.selectGls = [];
      this.dependencyChips = [];
      this.dependencyNeighborIds = [];
      this.dependencyWaitingOnIds = [];
      this.dependencyWaitingOnRows = [];
      this.removedWaitingOnIds = [];
      this.dependenciesExpanded = false;
      this.dependenciesExpandLocked = false;
      this.searchCustomFilter = '';
      this.purposeExpanded = false;
      this.instructionExpanded = false;
      this.activeStep = ADD_TASK_STEPS.DETAILS;
      this.allowedUserIds = [];
      this.usersFetchLoading = false;
      this.usersFetchToken += 1;

      (this.customColumns || []).forEach((col) => {
        const defaultVal = '';
        this.$set(this.addTaskData, col.columnName, defaultVal);
      });
    },

    close() {
      this.$emit('update:visible', false);
    },

    onDialogInput(val) {
      this.$emit('update:visible', val);
    },

    isStepDone(stepId) {
      return this.stepOrder.indexOf(stepId) < this.stepOrder.indexOf(this.activeStep);
    },

    onStepClick(stepId) {
      if (this.isStepDone(stepId) || stepId === this.activeStep) {
        this.activeStep = stepId;
      }
    },

    goPrevious() {
      const idx = this.stepOrder.indexOf(this.activeStep);
      if (idx > 0) this.activeStep = this.stepOrder[idx - 1];
    },

    async goNext() {
      const context = this.validationContext;
      const { valid, errors } = validateTaskStep(
        this.activeStep,
        this.addTaskData,
        context,
      );
      const stepKeys = getStepValidationKeys(this.activeStep, context);
      const nextErrors = { ...this.addTaskDataError };
      stepKeys.forEach((key) => {
        nextErrors[key] = errors[key] || '';
      });
      this.addTaskDataError = nextErrors;

      if (!valid) {
        this.$store.commit('sbError', this.assignmentValidationSnackbar(), { root: true });
        return;
      }

      if (this.activeStep === ADD_TASK_STEPS.DETAILS) {
        const accessOk = await this.runCycleAccessValidate();
        if (!accessOk) return;
      }

      const idx = this.stepOrder.indexOf(this.activeStep);
      if (idx < this.stepOrder.length - 1) {
        this.activeStep = this.stepOrder[idx + 1];
      }
    },

    /**
     * POST validateCycleTaskList with the same body shape as add/edit.
     * Paints fieldErrors on the existing error object.
     */
    async runCycleAccessValidate() {
      if (!this.projectId) return true;
      this.accessValidateLoading = true;
      try {
        const payload = this.buildValidatePayload();
        const mode = this.isEditMode ? 'edit' : 'add';
        const validation = await this.$store.dispatch('tasks/validateCycleTaskList', {
          checklistId: this.projectId,
          data: payload,
          mode,
        });
        const result = validation?.data || {};
        const fieldErrors = result.fieldErrors || {};
        const isValid = result.valid === true
          || (validation?.status === 'success' && !Object.keys(fieldErrors).length);

        if (isValid) {
          ['location', 'preparer', 'approver', 'sign_off_approvers', 'location_users'].forEach((key) => {
            if (this.addTaskDataError[key]) this.$set(this.addTaskDataError, key, '');
          });
          return true;
        }

        const next = { ...this.addTaskDataError };
        Object.entries(fieldErrors).forEach(([key, message]) => {
          next[key] = Array.isArray(message) ? message : [message];
        });
        this.addTaskDataError = next;
        const firstMsg = Object.values(fieldErrors).flat().find(Boolean)
          || validation?.message
          || 'Access validation failed';
        this.$store.commit('sbError', firstMsg, { root: true });
        return false;
      } catch (err) {
        this.$store.commit('sbError', err?.message || 'Access validation failed', { root: true });
        return false;
      } finally {
        this.accessValidateLoading = false;
      }
    },

    buildValidatePayload() {
      let selectglsLocal = [];
      if (this.isAccountReconciliation) {
        if (Array.isArray(this.addTaskData.__tblinks) && this.addTaskData.__tblinks.length > 0) {
          selectglsLocal = [...this.addTaskData.__tblinks];
        } else if (Array.isArray(this.addTaskData.__tbtotals?.items) && this.addTaskData.__tbtotals.items.length > 0) {
          selectglsLocal = this.addTaskData.__tbtotals.items.map((gl) => String(gl._id));
        } else if (Array.isArray(this.selectGls) && this.selectGls.length) {
          selectglsLocal = [...this.selectGls];
        }
      }

      const ndt = {
        ...this.addTaskData,
        auto_approve: !!this.addTaskData.auto_approve,
        evidence_required: coerceEvidenceRequired(this.addTaskData.evidence_required),
        preparer: [...(this.addTaskData.preparer || [])],
        approver: [...(this.addTaskData.approver || [])],
        requires_sign_off: !!this.addTaskData.requires_sign_off,
        sign_off_approvers: this.addTaskData.requires_sign_off
          ? [...(this.addTaskData.sign_off_approvers || [])]
          : [],
        ...glPayloadFromSelection(selectglsLocal, this.addTaskData),
      };
      if (!this.isEditMode) delete ndt._id;
      delete ndt.customColumns;
      return withCycleUserRoles(ndt);
    },

    async refreshAllowedUsersForLocation() {
      const locationId = this.addTaskData?.location;
      const locationIds = locationId ? [locationId] : [];
      if (!locationIds.length) {
        this.usersFetchToken += 1;
        this.usersFetchLoading = false;
        this.allowedUserIds = [];
        return;
      }

      const token = ++this.usersFetchToken;
      this.usersFetchLoading = true;
      try {
        const ids = await fetchUsersForLocations({
          locationIds,
          cycleId: this.projectId,
        });
        if (token !== this.usersFetchToken) return;
        this.allowedUserIds = ids;
      } catch (err) {
        if (token !== this.usersFetchToken) return;
        console.error('[AddTaskDialog]: usersForLocations error', err);
      } finally {
        if (token === this.usersFetchToken) {
          this.usersFetchLoading = false;
        }
      }
    },

    onRequiresSignOffToggle() {
      const next = !this.addTaskData.requires_sign_off;
      const nextData = {
        ...this.addTaskData,
        requires_sign_off: next,
        sign_off_approvers: next ? [...(this.addTaskData.sign_off_approvers || [])] : [],
      };
      nextData.user_roles = buildCycleUserRolesFromAssignments(nextData);
      this.addTaskData = nextData;
      if (!next) {
        this.$set(this.addTaskDataError, 'sign_off_approvers', '');
      }
    },

    onAutoApproveToggle() {
      this.addTaskData = {
        ...this.addTaskData,
        auto_approve: !this.addTaskData.auto_approve,
      };
    },

    onEvidenceRequiredToggle() {
      const evidence_required = !this.addTaskData.evidence_required;
      const next = resolveEvidenceFieldState({
        evidence_required,
        no_of_evidence: this.addTaskData.no_of_evidence,
      }, { fillDefaultWhenRequired: true, resetCountWhenNotRequired: true });
      this.addTaskData = {
        ...this.addTaskData,
        evidence_required,
        no_of_evidence: next.no_of_evidence,
      };
      this.$set(this.addTaskDataError, 'no_of_evidence', next.error);
    },

    onEnforceThresholdToggle() {
      this.addTaskData = {
        ...this.addTaskData,
        enforceThreshold: !this.addTaskData.enforceThreshold,
      };
    },

    onAssignmentRoleChange(field, value) {
      const synced = syncAssignmentRolesOnChange(this.addTaskData, field, value);
      this.addTaskData = {
        ...this.addTaskData,
        preparer: synced.preparer,
        approver: synced.approver,
        sign_off_approvers: synced.sign_off_approvers,
        requires_sign_off: synced.requires_sign_off,
        user_roles: synced.user_roles,
      };
      if (field === 'preparer' || field === 'approver' || field === 'sign_off_approvers') {
        this.$set(this.addTaskDataError, field === 'sign_off_approvers' ? 'sign_off_approvers' : field, '');
        if (field !== 'sign_off_approvers') {
          this.$set(this.addTaskDataError, 'location_users', '');
        }
      }
    },

    clearGlSelection() {
      this.selectGls = [];
      this.searchCustomFilter = '';
      this.addTaskData.__tbtotals = { total: 0, items: [] };
      this.addTaskData.__tblinks = [];
    },

    onTaskTypeChange(newType, oldType) {
      this.selectedTaskType = newType || '';
      this.$set(this.addTaskDataError, 'task_type', '');
      this.activeStep = ADD_TASK_STEPS.DETAILS;

      if (newType === this.TASK_TYPES.ACCOUNT_RECONCILIATION) {
        const category = String(this.addTaskData.category || '').trim();
        const subcategory = String(this.addTaskData.subcategory || '').trim();
        if (category && !this.coaCategoryOptions.includes(category)) {
          this.addTaskData.category = '';
        }
        if (subcategory && !this.coaSubcategoryOptions.includes(subcategory)) {
          this.addTaskData.subcategory = '';
        }
        return;
      }

      if (
        newType === this.TASK_TYPES.NORMAL
        || isChecklistTask(newType)
        || oldType === this.TASK_TYPES.ACCOUNT_RECONCILIATION
      ) {
        this.clearGlSelection();
      }
    },

    onEvidenceInput(val) {
      const next = resolveEvidenceFieldState({
        evidence_required: this.addTaskData.evidence_required,
        no_of_evidence: val,
      });
      this.addTaskData.no_of_evidence = next.no_of_evidence;
      this.$set(this.addTaskDataError, 'no_of_evidence', next.error);
    },

    onEvidenceCountKeypress(e) {
      if (!isEvidenceCountKeyAllowed(e, this.addTaskData.no_of_evidence)) e.preventDefault();
    },

    onEvidenceCountPaste(e) {
      e.preventDefault();
      const pasted = parsePastedEvidenceCount(e.clipboardData && e.clipboardData.getData('text'));
      if (pasted == null) return;
      this.onEvidenceInput(pasted);
    },

    formatError(err) {
      if (Array.isArray(err)) return err[0] || '';
      return err || '';
    },

    assignmentValidationSnackbar() {
      const hasInactiveAssignment = formHasInactiveAssignmentUsers(
        this.addTaskData,
        this.inactiveUserIds,
      );
      if (hasInactiveAssignment) {
        return this.validationContext.errorMessages?.INACTIVE_ASSIGNMENT_USERS
          || 'Please remove inactive users before saving.';
      }
      return 'Please fill all mandatory fields';
    },

    setServerErrors(errors) {
      this.addTaskDataError = errors || {};
      if (this.addTaskDataError.duedate) {
        this.activeStep = ADD_TASK_STEPS.DETAILS;
      }
      const first = Object.values(this.addTaskDataError)
        .map((value) => this.formatError(value))
        .find(Boolean);
      if (first) this.$store.commit('sbError', first, { root: true });
    },

    notifyTaskCreated({ task, projectId, userType, tasklistView }) {
      if (!task?._id) return;

      this.$store.commit('sbSuccess', {
        text: 'Task created successfully',
        actionLabel: 'View',
        actionPayload: {
          type: 'view-task',
          task,
          checklistId: projectId,
          userType,
          tasklistView,
          testId: 'tasklist__task-created-view-btn',
        },
      });
    },

    async onSave() {
      const { valid, errors } = validateTaskFields(this.addTaskData, this.validationContext);
      this.addTaskDataError = errors;
      if (!valid) {
        this.$store.commit('sbError', this.assignmentValidationSnackbar(), { root: true });
        return;
      }

      // Checklist has no Accounts step — validate access on save. AR already validated on Next.
      if (this.isChecklistTaskType) {
        const accessOk = await this.runCycleAccessValidate();
        if (!accessOk) return;
      }

      if (this.dependencyChips.length && chipsHaveMissingLinkType(this.dependencyChips)) {
        this.activeStep = ADD_TASK_STEPS.DETAILS;
        this.$store.commit(
          'sbError',
          CYCLE_TASK_DEPENDENCY.SAVE_LINK_TYPE_REQUIRED,
          { root: true },
        );
        return;
      }

      let selectglsLocal = [];
      if (this.isAccountReconciliation) {
        if (Array.isArray(this.addTaskData.__tblinks) && this.addTaskData.__tblinks.length > 0) {
          selectglsLocal = [...this.addTaskData.__tblinks];
        } else if (Array.isArray(this.addTaskData.__tbtotals?.items) && this.addTaskData.__tbtotals.items.length > 0) {
          selectglsLocal = this.addTaskData.__tbtotals.items.map((gl) => String(gl._id));
        }
      }

      const addTaskData = {
        ...this.addTaskData,
        auto_approve: !!this.addTaskData.auto_approve,
        evidence_required: coerceEvidenceRequired(this.addTaskData.evidence_required),
        enforceThreshold: normalizeEnforceThresholdForTaskType(
          this.addTaskData.task_type,
          this.isAccountReconciliation ? !!this.addTaskData.enforceThreshold : false,
        ),
        preparer: [...(this.addTaskData.preparer || [])],
        approver: [...(this.addTaskData.approver || [])],
        requires_sign_off: !!this.addTaskData.requires_sign_off,
        sign_off_approvers: this.addTaskData.requires_sign_off
          ? [...(this.addTaskData.sign_off_approvers || [])]
          : [],
        links: linksPayloadFromChips(this.dependencyChips),
        unlinkWaitingOn: this.removedWaitingOnIds.slice(),
      };

      this.$emit('save', {
        addTaskData,
        customColumns: this.resolvedCustomColumns,
        selectglsLocal,
        projectId: this.projectId,
      });
    },
  },
};
</script>
